package com.shadowings.gero.presentation.component

/**
 * Pure parsing for the Gardiner-code mini-language rendered by [MdCWord]:
 * `word := column ("-" column)*`, `column := row (":" row)*`,
 * `row := cells [("h"|"w")number]{0,2}`, `cells := code ("*" code)*`.
 * Mirrors the grammar/ordering in MdCWord.kt exactly (columns reversed if
 * [rtl], cells-within-a-row reversed if [rtl], rows never reversed), so a
 * flattened index here always matches the cell MdCWord itself would draw
 * at that position.
 */

data class GardinerCell(val code: String, val flatIndex: Int)

data class GardinerRow(
    val cells: List<GardinerCell>,
    val heightWeight: Float,
    val widthWeight: Float,
)

data class GardinerColumn(val rows: List<GardinerRow>)

private val ROW_MODIFIER_SUFFIX_REGEX = Regex("^(.*)([hw])(\\d+(?:\\.\\d+)?)$")

private data class GardinerRowModifiers(
    val cellsWord: String,
    val heightWeight: Float,
    val widthWeight: Float,
)

private fun parseRowModifiers(rowWord: String): GardinerRowModifiers {
    var cellsWord = rowWord
    var heightWeight = 1f
    var widthWeight = 1f
    repeat(2) {
        val match = ROW_MODIFIER_SUFFIX_REGEX.matchEntire(cellsWord) ?: return@repeat
        val (base, kind, numberPart) = match.destructured
        val value = numberPart.toFloatOrNull()?.takeIf { it > 0f } ?: 1f
        when (kind) {
            "h" -> heightWeight = value
            "w" -> widthWeight = value
        }
        cellsWord = base
    }
    return GardinerRowModifiers(cellsWord, heightWeight, widthWeight)
}

fun parseGardinerWord(word: String, rtl: Boolean = false): List<GardinerColumn> {
    val columns = if (!rtl) word.split("-") else word.split("-").reversed()
    var flatIndex = 0

    return columns.map { columnWord ->
        val rows = columnWord.split(":").map { rowWord ->
            val (cellsWord, heightWeight, widthWeight) = parseRowModifiers(rowWord)
            val cellCodes = cellsWord.split("*")
            val orderedCodes = if (!rtl) cellCodes else cellCodes.reversed()
            val cells = orderedCodes.map { code ->
                GardinerCell(code = code, flatIndex = flatIndex++)
            }
            GardinerRow(cells = cells, heightWeight = heightWeight, widthWeight = widthWeight)
        }
        GardinerColumn(rows = rows)
    }
}

fun flattenGardinerCodes(word: String, rtl: Boolean = false): List<String> =
    parseGardinerWord(word, rtl).flatMap { column -> column.rows.flatMap { row -> row.cells.map { it.code } } }

/**
 * Heuristic for "is this text a Gardiner-notation word (to render as a glyph via [MdCWord])
 * rather than a plain translation/transliteration string" — used both to pick a quiz option's
 * render style and, in [com.shadowings.gero.domain.model.WrongAnswerRecordQuizMapper], to avoid
 * mixing glyph-shaped and text-shaped correct answers as distractors for each other.
 */
fun looksLikeGardinerCode(text: String): Boolean =
    text.isNotBlank() &&
        text.all { it.isLetterOrDigit() || it == '-' || it == ':' || it == '*' || it == '.' } &&
        (text.any { it.isDigit() } || text.contains("-") || text.contains(":") || text.contains("*"))
